"use client";
import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	TextField,
	Typography,
} from "@mui/material";
import { Controller, Form, SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, memo, useImperativeHandle } from "react";

const RegisterDocumentFormSchema = z.object({
	email: z.string().email({ message: "Email không hợp lệ." }),
	fullName: z
		.string()
		.min(1, { message: "Vui lòng nhập họ và tên." })
		.min(6, { message: "Họ và tên tối thiểu 6 ký tự." }),
	phoneNumber: z
		.string()
		.min(1, "Vui lòng nhập số điện thoại.")
		.regex(/^\+?[0-9]{10,20}$/, {
			message: "Số điện thoại không hợp lệ.",
		}),
	company: z.string().min(1, "Vui lòng nhập tên công ty."),
	job: z.string().min(1, "Vui lòng nhập tên công việc"),
	learningDifficulty: z.string().min(1, "Vui lòng nhập khó khăn"),
	isSubscribeEmailMKT: z.boolean().default(true),
});
type RegisterDocumentFormFields = z.infer<typeof RegisterDocumentFormSchema>;

export interface RegisterDocumentFormProps {
	className?: string;
	onSubmit?: (formData: RegisterDocumentFormFields) => void;
	onCancel?: () => void;
}
export interface RegisterDocumentFormRef {
	submit: () => void;
	cancel: () => void;
}
const RegisterDocumentForm = forwardRef<
	RegisterDocumentFormRef,
	RegisterDocumentFormProps
>(({ className, onSubmit, onCancel }, ref) => {
	const { control, handleSubmit, reset } = useForm<RegisterDocumentFormFields>({
		defaultValues: {
			email: "",
			fullName: "",
			phoneNumber: "",
			company: "",
			learningDifficulty: "",
			job: "",
			isSubscribeEmailMKT: true,
		},
		resolver: zodResolver(RegisterDocumentFormSchema),
	});

	useImperativeHandle(ref, () => {
		return {
			submit: () => {
				onSubmit && handleSubmit(onSubmit)();
			},
			cancel: () => {
				reset?.();
			},
		};
	});

	return (
		<Box component="div" className={className}>
			<Form control={control} className="flex flex-col gap-3">
				<Controller
					control={control}
					name="fullName"
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Họ và tên"
							placeholder="Nhập họ và tên"
							required
							error={!!error?.message}
							helperText={error?.message}
							{...field}
						/>
					)}
				/>
				<Controller
					control={control}
					name="email"
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Email"
							required
							error={!!error?.message}
							helperText={error?.message}
							placeholder="Email"
							{...field}
						/>
					)}
				/>
				<Controller
					control={control}
					name="phoneNumber"
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Điện thoại"
							placeholder="Nhập số điện thoại"
							error={!!error?.message}
							helperText={error?.message}
							required
							{...field}
						/>
					)}
				/>
				<Controller
					control={control}
					name="company"
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Công ty"
							error={!!error?.message}
							helperText={error?.message}
							required
							placeholder="Nhập tên công ty"
							{...field}
						/>
					)}
				/>
				<Controller
					control={control}
					name="job"
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Công việc hiện tại của anh/chị là gì?"
							error={!!error?.message}
							helperText={error?.message}
							required
							placeholder="Nhập tên công việc"
							{...field}
						/>
					)}
				/>
				<Controller
					control={control}
					name="learningDifficulty"
					render={({ field, fieldState: { error } }) => (
						<TextField
							multiline
							label="Khó khăn của anh/chị liên quan đến L&D trong tổ chức?"
							error={!!error?.message}
							helperText={error?.message}
							required
							placeholder="Nhập khó khăn"
							{...field}
						/>
					)}
				/>
				{/* <Controller
					control={control}
					name="isSubscribeEmailMKT"
					render={({ field, fieldState: { error } }) => (
						<FormControl required error={!!error?.message}>
							<FormControlLabel
								className="items-start !mr-0"
								control={
									<Checkbox checked={field.value} onChange={field.onChange} />
								}
								label={
									<Typography
										component="p"
										className="text-pretty text-sm text-justify"
									>
										Đăng ký nhận bản tin từ <strong>L&D Vietnam</strong> để cập
										nhật những góc nhìn học tập độc quyền, xu hướng phát triển
										nghề nghiệp và tài nguyên thực tiễn được gửi thẳng tới hộp
										thư của bạn.
									</Typography>
								}
							/>
							<FormHelperText>{error?.message}</FormHelperText>
						</FormControl>
					)}
				/> */}
			</Form>
		</Box>
	);
});
export default memo(RegisterDocumentForm);
